import { I18n } from '@calendly/i18n'
import { BodyText } from '@calendly/ui/components/typography'

import { RECAP_ACCESS_LEVELS, RecapPermission } from 'features/recaps/constants'
import { useRecapShare } from 'features/recaps/meeting_list/meeting_card/recap_share_context'

import { GroupAccessListItem } from './group_access_list_item'
import { OrganizationAccessListItem } from './organization_access_list_item'
import {
  AccessList as AccessListContainer,
  AccessListSectionContainer,
  AccessListSectionsContainer,
  accessListSectionHeaderCss,
} from './styles'
import { UserAccessListItem } from './user_access_list_item'

const AccessListSection = ({
  title,
  children,
  testId,
}) => (
  <AccessListSectionContainer data-testid={testId}>
    {title ? (
      <BodyText weight="bold" size="large" className={accessListSectionHeaderCss}>
        {title}
      </BodyText>
    ) : null}
    <AccessListContainer>
      {children}
    </AccessListContainer>
  </AccessListSectionContainer>
)

const buildCurrentUserRow = (currentUser) => ({
  uuid: currentUser.uuid,
  name: currentUser.profile?.name ?? currentUser.name ?? '',
  email: currentUser.email ?? '',
  avatarUrl: currentUser.avatar_url ?? null,
  avatarLetter: currentUser.avatar_letter ?? null,
  isCurrentUser: true,
})

export const AccessList = () => {
  const {
    recapPermission,
    currentUser,
    accessLevel: { value: accessLevel },
    accessList: {
      owner,
      users,
      userGroups,
      organization,
      isLoading,
    },
    permissionUpdates: {
      getUserRelation,
      getUserGroupRelation,
    },
  } = useRecapShare()

  if (isLoading) {
    return null
  }

  const isRestricted = accessLevel === RECAP_ACCESS_LEVELS.RESTRICTED
  const showSharerSelfRow = isRestricted && recapPermission === RecapPermission.SHARE && currentUser

  const visibleUsers = users.filter((user) => {
    const relation = getUserRelation(user.uuid, user.relation)
    return relation !== null
  })

  const grantUsers = showSharerSelfRow
    ? visibleUsers.filter((user) => user.uuid !== currentUser.uuid)
    : visibleUsers

  const visibleUserGroups = userGroups.filter((userGroup) => {
    const relation = getUserGroupRelation(userGroup.uuid, userGroup.relation)
    return relation !== null
  })

  const showGrantedEntries = isRestricted && (grantUsers.length > 0 || visibleUserGroups.length > 0)

  return (
    <AccessListSectionsContainer>
      <AccessListSection testId="access-list-people-section">
        {owner ? (
          <UserAccessListItem
            user={owner}
            testId="access-list-owner"
            variant="owner"
          />
        ) : null}
        {showSharerSelfRow ? (
          <UserAccessListItem
            user={buildCurrentUserRow(currentUser)}
            testId="access-list-current-user"
            variant="currentSharer"
          />
        ) : null}
        {showGrantedEntries && (
          <>
            {grantUsers.map((user) => (
              <UserAccessListItem
                key={user.uuid}
                user={user}
                testId={`access-list-user-${user.uuid}`}
                variant="grant"
              />
            ))}
            {visibleUserGroups.map((userGroup) => (
              <GroupAccessListItem
                key={userGroup.uuid}
                userGroup={userGroup}
              />
            ))}
          </>
        )}
      </AccessListSection>
      {organization ? (
        <AccessListSection
          title={I18n.t('publisher.meeting_recap.share_modal.access_list.general_access.header')}
          testId="access-list-general-access-section"
        >
          <OrganizationAccessListItem />
        </AccessListSection>
      ) : null}
    </AccessListSectionsContainer>
  )
}
